import { useRewards } from "@/api/orval/reward/reward";
import { useTranslation } from "@/core/i18n";
import { RewardStatus } from "api/types";

/** A tier reward the patient can use now on the tier they hold. */
export type HeldTierReward = {
  readonly rewardId: string;
  readonly title: string;
  /** "2 uses left" for a limited tier reward; null for one they can use anytime. */
  readonly usesLeft: string | null;
};

/**
 * The tier rewards the patient can use now, in the order the API lists them. Reads the rewards
 * list the rewards pages load, so it shares their cache; it only asks while the patient holds a tier.
 */
export const useHeldTierRewards = (holdsTier: boolean): readonly HeldTierReward[] => {
  const { t } = useTranslation();
  const { data: rewards } = useRewards(undefined, { query: { enabled: holdsTier } });
  return (rewards ?? []).flatMap((reward): HeldTierReward[] => {
    const { tierReward } = reward;
    if (reward.status !== RewardStatus.Available || !tierReward) return [];
    if (!reward.title) {
      throw new Error(`Tier reward ${reward.rewardId} has no title`);
    }
    if (tierReward.maxRedemptionsPerClient === null) {
      return [{ rewardId: reward.rewardId, title: reward.title, usesLeft: null }];
    }
    if (tierReward.usesRemaining === null) {
      throw new Error(
        `Limited tier reward ${reward.rewardId} came back without its uses remaining`,
      );
    }
    return [
      {
        rewardId: reward.rewardId,
        title: reward.title,
        usesLeft: t("tierRewards.usesLeft", { count: tierReward.usesRemaining }),
      },
    ];
  });
};
